import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { useProjects } from '../hooks/useProjects';
import { useContacts } from '../hooks/useContacts';
import { useAgentHealth } from '../hooks/useAgentHealth';
import useProjectStore from '../stores/projectStore';
import useAuthStore from '../stores/authStore';
import StatCard from '../components/ui/StatCard';

// ─── Hooks: globale Aggregate ──────────────────────────────────────────────
function useGlobalProjectReports() {
  return useQuery({
    queryKey: ['project-reports', 'global'],
    queryFn: async () => {
      const { data } = await api.get('/project-reports', { skipProject: true });
      return data.reports || [];
    },
  });
}

function useAnalyticsOverview() {
  return useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => {
      const { data } = await api.get('/analytics/overview', { skipProject: true });
      return data;
    },
  });
}

// ─── Mini-Charts: inline-SVG, ohne Lib ─────────────────────────────────────
function Sparkline({ values = [], color = '#0C9488', height = 36, width = 120 }) {
  const cleaned = values.filter((v) => v != null && !Number.isNaN(v));
  if (cleaned.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-[10px] text-gray-300"
        style={{ width, height }}
      >
        zu wenig Daten
      </div>
    );
  }
  const min = Math.min(...cleaned);
  const max = Math.max(...cleaned);
  const range = max - min || 1;
  const step = width / (cleaned.length - 1);
  const points = cleaned.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-${color.replace('#', '')})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      {points.length > 0 && (
        <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2.5" fill={color} />
      )}
    </svg>
  );
}

function MiniBars({ values = [], color = '#0D3B50', height = 36, width = 120, gap = 2 }) {
  if (!values.length) {
    return (
      <div className="flex items-center justify-center text-[10px] text-gray-300" style={{ width, height }}>
        zu wenig Daten
      </div>
    );
  }
  const max = Math.max(...values, 1);
  const barW = (width - gap * (values.length - 1)) / values.length;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * (height - 2));
        const x = i * (barW + gap);
        const y = height - h;
        return <rect key={i} x={x} y={y} width={barW} height={h} rx="1.5" fill={color} opacity={0.85} />;
      })}
    </svg>
  );
}

function DonutScore({ value = 0, size = 56, stroke = 6, color = '#0C9488' }) {
  const v = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDECEA" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${c}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" className="font-bold" style={{ fontSize: size * 0.32 }}>
        {Math.round(v)}
      </text>
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function initials(name) {
  return (name || '?')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(d));
  } catch {
    return '—';
  }
}

function scoreColor(s) {
  if (s == null) return '#9CA3AF';
  if (s >= 80) return '#10B981';
  if (s >= 60) return '#0C9488';
  if (s >= 40) return '#F59E0B';
  return '#EF4444';
}

// Kleiner Zähler-Kasten für die Agenten-Health (Fehler/Datenquellen/Überfällig).
function HealthStat({ label, value, color }) {
  const v = value ?? 0;
  return (
    <div className="rounded-lg border border-black/[.06] px-2 py-2 text-center">
      <div className="text-[18px] font-bold tabular-nums leading-none" style={{ color: v > 0 ? color : '#9CA3AF' }}>
        {v}
      </div>
      <div className="text-[9.5px] uppercase tracking-wide text-gray-400 mt-1">{label}</div>
    </div>
  );
}

// Extrahiert eine numerische Score-Reihe aus einer Reports-Liste (jüngste zuletzt).
function reportTrend(reports = [], pick = (r) => r?.data?.cro?.avg_score) {
  return [...reports]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map(pick)
    .map((v) => (typeof v === 'string' ? Number(v) : v))
    .filter((v) => v != null && !Number.isNaN(v));
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function PlatformHome() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const { data: projectData, isLoading: projectsLoading } = useProjects();
  const { data: contactsData } = useContacts();
  const { data: reports = [] } = useGlobalProjectReports();
  const { data: analytics } = useAnalyticsOverview();
  const { data: agentHealth } = useAgentHealth();

  const projects = projectData?.projects || [];
  const contacts = contactsData?.data || [];
  const crmContacts = contacts.filter((c) => c.crm_contact_id);

  // Aggregate
  const totalAnalyses = projects.reduce((s, p) => s + (p.analyses_count || 0), 0);
  const totalKeywords = projects.reduce((s, p) => s + (p.seo_keywords_count || 0), 0);
  const totalMeasures = projects.reduce((s, p) => s + (p.seo_measures_count || 0), 0);

  // Performance pro Projekt: nimm den jüngsten Report und dessen CRO-/SEO-Werte
  const projectPerformances = useMemo(() => {
    return projects
      .map((p) => {
        const projectReports = reports.filter((r) => r.project_id === p.id);
        const latest = projectReports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        const croScore = latest?.data?.cro?.avg_score ?? null;
        const seoVisibility = latest?.data?.seo?.visibility_index ?? latest?.data?.seo?.avg_position ?? null;
        const trend = reportTrend(projectReports);
        return {
          project: p,
          latest,
          croScore: croScore != null ? Number(croScore) : null,
          seoVisibility,
          trend,
          analyses: p.analyses_count || 0,
        };
      })
      .sort((a, b) => (b.croScore ?? -1) - (a.croScore ?? -1));
  }, [projects, reports]);

  // Letzte Reports global (max 5)
  const latestReports = useMemo(
    () =>
      [...reports]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
        .map((r) => ({
          ...r,
          project: projects.find((p) => p.id === r.project_id),
        })),
    [reports, projects]
  );

  // Reports pro Monat (letzte 6 Monate, für Bar-Chart)
  const reportsPerMonth = useMemo(() => {
    const buckets = new Map();
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.set(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, { label: d.toLocaleString('de-DE', { month: 'short' }), value: 0 });
    }
    reports.forEach((r) => {
      const d = new Date(r.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (buckets.has(key)) buckets.get(key).value += 1;
    });
    return Array.from(buckets.values());
  }, [reports]);

  const handleOpenProject = (id, path = '/') => {
    setCurrentProject(id);
    navigate(path);
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Gute Nacht';
    if (h < 11) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  }, []);
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div>
      {/* ─── Header ─── */}
      <div className="mb-5">
        <h1 className="text-[23px] font-bold mb-0.5">
          {greeting}{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-gray-500 text-[13.5px]">
          {projects.length > 0
            ? `${projects.length} Projekte · ${contacts.length} Kunden · ${reports.length} Reports gesamt`
            : 'Plattform-Übersicht'}
        </p>
      </div>

      {/* ─── Standard-Widgets (je 50%): Reporting-Aktivität + Agenten ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
        {/* Reporting-Aktivität */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h2 className="font-semibold text-[15px] mb-1">Reporting-Aktivität</h2>
          <p className="text-[11.5px] text-gray-400 mb-4">Monatsreports der letzten 6 Monate</p>
          <div className="flex items-end gap-3 mb-3">
            <div className="text-[28px] font-bold leading-none">{reports.length}</div>
            <div className="text-[11.5px] text-gray-500 pb-1">Reports gesamt</div>
          </div>
          <MiniBars values={reportsPerMonth.map((m) => m.value)} color="#0D3B50" width={240} height={64} gap={6} />
          <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
            {reportsPerMonth.map((m, i) => (
              <span key={i}>{m.label}</span>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-gray-500">Ø Score (alle Projekte)</span>
              <span className="font-semibold text-gray-900">
                {(() => {
                  const scores = projectPerformances.map((p) => p.croScore).filter((s) => s != null);
                  if (!scores.length) return '—';
                  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
                })()}
              </span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-gray-500">Aktive Kunden</span>
              <span className="font-semibold text-gray-900">{contacts.length}</span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-gray-500">Default-LLM</span>
              <span className="font-mono text-[11px] text-gray-700">{analytics?.default_provider || '—'}</span>
            </div>
          </div>
        </div>

        {/* Agenten */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold text-[15px]">Agenten</h2>
            <button onClick={() => navigate('/systemstatus')} className="text-[12px] text-teal font-medium hover:underline">
              Systemstatus →
            </button>
          </div>
          <p className="text-[11.5px] text-gray-400 mb-4">Status über alle Projekte</p>

          <div className="flex items-end gap-3 mb-4">
            <div className="text-[28px] font-bold leading-none">{agentHealth?.summary?.active ?? '—'}</div>
            <div className="text-[11.5px] text-gray-500 pb-1">
              aktive Agenten
              {agentHealth?.summary?.running ? ` · ${agentHealth.summary.running} laufen gerade` : ''}
            </div>
          </div>

          {/* Fehler-Zähler */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <HealthStat label="Fehlgeschl." value={agentHealth?.summary?.failed} color="#dc2626" />
            <HealthStat label="Datenquellen" value={agentHealth?.summary?.integration_errors} color="#b8860b" />
            <HealthStat label="Überfällig" value={agentHealth?.summary?.stale} color="#6b7280" />
          </div>

          {/* Detail: betroffene Agenten oder OK-Zustand */}
          {agentHealth?.summary?.ok ? (
            <div className="flex items-center gap-2 text-[12px] text-teal bg-teal-light px-3 py-2 rounded-lg">
              <span>✅</span>
              <span>Alles in Ordnung — keine Fehler.</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              {(agentHealth?.failed_agents || []).slice(0, 3).map((r, i) => (
                <div key={`fa-${i}`} className="flex items-start gap-2 text-[12px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-red-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium text-gray-800">{r.project}</span>
                    <span className="text-gray-400"> · {r.agent}</span>
                    {r.error && <div className="text-[11px] text-red-600 truncate">{r.error}</div>}
                  </div>
                </div>
              ))}
              {(agentHealth?.integration_errors || []).slice(0, 2).map((r, i) => (
                <div key={`ie-${i}`} className="flex items-start gap-2 text-[12px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-amber-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium text-gray-800">{r.project}</span>
                    <span className="text-gray-400"> · {r.type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TEMP DEAKTIVIERT (nicht gelöscht) — KPI-Strip, Kundenübersicht und
          „Letzte Reportings". Werden via {false && …} geparkt, bis die
          Dashboard-Inhalte final definiert sind. Zum Reaktivieren: false→true
          bzw. den Block wieder auspacken.
          ═══════════════════════════════════════════════════════════════════ */}
      {false && (
        <>
          {/* ─── KPI-Strip ─── */}
          <div className="flex gap-3.5 mb-6 flex-wrap">
            <StatCard
              label="Kunden"
              value={contacts.length || '—'}
              sub={crmContacts.length ? `${crmContacts.length} aus CRM` : 'aktive Kunden'}
              icon="◉" iconBg="#E6F4F2" iconColor="#0C9488"
            />
            <StatCard
              label="Projekte"
              value={projects.length || '—'}
              sub={`${projects.filter((p) => p.is_default).length} als Default`}
              icon="⌘" iconBg="#DBEAFE" iconColor="#3B82F6"
            />
            <StatCard
              label="Analysen 30T"
              value={analytics?.analyses_30d ?? '—'}
              sub={`${totalAnalyses} insgesamt`}
              icon="⟳" iconBg="#EDE9FE" iconColor="#8B5CF6"
            />
            <StatCard
              label="Keywords"
              value={totalKeywords || '—'}
              sub={`${totalMeasures} SEO-Maßnahmen`}
              icon="◎" iconBg="#FEF3C7" iconColor="#B45309"
            />
            <StatCard
              label="Reports"
              value={reports.length || '—'}
              sub={latestReports[0] ? `zuletzt ${formatDate(latestReports[0].created_at)}` : ''}
              icon="▦" iconBg="#D1FAE5" iconColor="#10B981"
            />
            <StatCard
              label="Tokens (30T)"
              value={analytics?.token_usage ? Number(analytics.token_usage).toLocaleString('de-DE') : '—'}
              sub={analytics?.pdf_exports != null ? `${analytics.pdf_exports} PDF-Exporte` : ''}
              icon="◈" iconBg="#FEE2E2" iconColor="#DC2626"
            />
          </div>

          {/* ─── Kundenübersicht ─── */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-[15px]">Kundenübersicht</h2>
                <p className="text-[11.5px] text-gray-400 mt-0.5">Performance je Kundenprojekt — sortiert nach CRO-Score</p>
              </div>
              <button onClick={() => navigate('/projects')} className="text-[12px] text-teal font-medium hover:underline">
                alle Projekte →
              </button>
            </div>

            {projectsLoading ? (
              <div className="py-10 text-center text-gray-400 text-sm">Wird geladen…</div>
            ) : projectPerformances.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">Noch keine Projekte angelegt.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {projectPerformances.slice(0, 6).map(({ project, croScore, trend, analyses, latest }) => (
                  <button
                    key={project.id}
                    onClick={() => handleOpenProject(project.id, '/')}
                    className="w-full flex items-center gap-4 py-3 hover:bg-gray-50/60 rounded-lg px-2 -mx-2 text-left transition-colors"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-[13px] font-bold text-white shrink-0"
                      style={{ backgroundColor: project.color || '#0C9488' }}
                    >
                      {initials(project.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-[13.5px] text-gray-900 truncate">{project.name}</div>
                        {project.is_default && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                            DEFAULT
                          </span>
                        )}
                      </div>
                      {project.domain && (
                        <div className="text-[11px] text-gray-400 truncate font-mono">{project.domain}</div>
                      )}
                    </div>
                    <div className="hidden md:flex flex-col items-end shrink-0 w-[140px]">
                      <Sparkline values={trend} color={scoreColor(croScore)} width={120} height={32} />
                      <div className="text-[10px] text-gray-400 mt-0.5">Score-Trend (Reports)</div>
                    </div>
                    <div className="flex flex-col items-end shrink-0 w-[64px]">
                      {croScore != null ? (
                        <DonutScore value={croScore} color={scoreColor(croScore)} />
                      ) : (
                        <div className="w-[56px] h-[56px] flex items-center justify-center text-[10px] text-gray-300">
                          kein Score
                        </div>
                      )}
                    </div>
                    <div className="hidden sm:flex flex-col items-end shrink-0 w-[70px]">
                      <div className="text-[14px] font-bold text-gray-900">{analyses}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide">Analysen</div>
                    </div>
                    <div className="hidden lg:flex flex-col items-end shrink-0 w-[90px]">
                      <div className="text-[12px] text-gray-700">
                        {latest?.data?.period?.label || '—'}
                      </div>
                      <div className="text-[10px] text-gray-400">letzter Report</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ─── Letzte Reportings ─── */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-[15px]">Letzte Reportings</h2>
                <p className="text-[11.5px] text-gray-400 mt-0.5">Die 5 jüngsten Monatsreports — projektübergreifend</p>
              </div>
            </div>

            {latestReports.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">Noch keine Reports erzeugt.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {latestReports.map((r) => {
                  const croScore = r.data?.cro?.avg_score != null ? Number(r.data.cro.avg_score) : null;
                  const projectColor = r.project?.color || '#0C9488';
                  return (
                    <button
                      key={r.id}
                      onClick={() => {
                        if (r.project_id) setCurrentProject(r.project_id);
                        navigate('/project-reports');
                      }}
                      className="w-full flex items-center gap-4 py-3 hover:bg-gray-50/60 rounded-lg px-2 -mx-2 text-left transition-colors"
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-[12px] font-bold text-white shrink-0"
                        style={{ backgroundColor: projectColor }}
                      >
                        {initials(r.project?.name || '?')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13px] text-gray-900 truncate">
                          {r.project?.name || `Projekt #${r.project_id}`}
                        </div>
                        <div className="text-[11px] text-gray-400 truncate">
                          {r.data?.period?.label ? `Periode ${r.data.period.label} · ` : ''}
                          erzeugt {formatDate(r.created_at)}
                        </div>
                      </div>
                      <div className="hidden md:flex items-center gap-2 shrink-0">
                        <div className="text-[10px] text-gray-400">CRO</div>
                        <div
                          className="text-[12px] font-bold px-2 py-0.5 rounded-md"
                          style={{
                            background: croScore != null ? `${scoreColor(croScore)}15` : '#F3F4F6',
                            color: croScore != null ? scoreColor(croScore) : '#9CA3AF',
                          }}
                        >
                          {croScore != null ? croScore : '—'}
                        </div>
                      </div>
                      <div className="hidden lg:block shrink-0">
                        <Sparkline
                          values={reportTrend(reports.filter((x) => x.project_id === r.project_id))}
                          color={scoreColor(croScore)}
                          width={100}
                          height={28}
                        />
                      </div>
                      <div className="text-gray-300 text-[14px] shrink-0">›</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
